import { collectFiles } from '../../../../../utils/file';
import {
  CreateDataChangeRequestInput,
  FieldType,
  ObjectFieldChangesInput,
  ObjectFieldValue,
  ObjectFieldValueFragment,
  RequestFormFragment,
  RequestFormType,
} from '../../../__generated__/graphql';
import { ExtraFieldInternalValue } from '../../ExtraFields/extraFieldInternalValue';
import { PositionChangeData } from './components/positionChange/types';
import { SalaryChangeData } from './components/salaryChange';
import { RequestFormData } from './types';

export async function mapToCreateDataChangeRequestInput(
  initiatorEmployeeId: string,
  employeeRecordFields: readonly ObjectFieldValueFragment[],
  formData: RequestFormData,
  requestForm: RequestFormFragment,
): Promise<CreateDataChangeRequestInput> {
  if (!formData.requestFormId || !formData.employeeId) {
    throw new Error('Request form ID and employee ID are required');
  }

  const filesToCollect = formData.files || [];
  const collectedFiles = await collectFiles(filesToCollect);

  const objectFieldChanges: ObjectFieldChangesInput | null =
    await mapToObjectFieldChangesInput(
      employeeRecordFields,
      formData,
      requestForm,
    );
  return {
    initiatorEmployeeId,
    employeeId: formData.employeeId,
    requestFormId: formData.requestFormId,
    payload: {
      objectsFieldsChanges:
        objectFieldChanges !== null ? [objectFieldChanges] : null,
      changeReason: formData.reason || '',
      files: collectedFiles.map((file) => ({
        fileName: file.fileName,
        fileContentBase64: file.fileBase64,
      })),
    },
  };
}

export async function mapToObjectFieldChangesInput(
  employeeRecordFields: readonly ObjectFieldValueFragment[],
  formData: Pick<
    RequestFormData,
    'employeeId' | 'fields' | 'positionChange' | 'salaryChange' | 'extraFields'
  >,
  requestForm: RequestFormFragment,
): Promise<ObjectFieldChangesInput | null> {
  if (!formData.employeeId) {
    throw new Error('Employee ID is required');
  }
  if (requestForm.flair__Request_Type__c === RequestFormType.NoObject) {
    return null;
  }
  if (!requestForm.flair__Data_Change_Object_Name__c) {
    throw new Error('Data change object name is required');
  }
  return {
    recordId: null,
    objectName: requestForm.flair__Data_Change_Object_Name__c,
    fieldChanges: mapFieldChanges(employeeRecordFields, formData, requestForm),
    positionChange: formData.positionChange
      ? mapToPositionChangeInput(formData.positionChange)
      : null,
    salaryChange: formData.salaryChange
      ? mapToSalaryChangeInput(formData.salaryChange)
      : null,
  };
}

const mapFieldChanges = (
  employeeRecordFields: readonly ObjectFieldValueFragment[],
  formData: Pick<
    RequestFormData,
    'employeeId' | 'fields' | 'positionChange' | 'salaryChange' | 'extraFields'
  >,
  requestForm: RequestFormFragment,
): ObjectFieldChangesInput['fieldChanges'] => {
  if (requestForm.flair__Request_Type__c === RequestFormType.ObjectDataChange) {
    return Object.entries(formData.fields || {}).map(
      ([fieldName, newValue]) => {
        const fieldInfo = employeeRecordFields.find(
          (field) => field.fieldInfo.name === fieldName,
        );
        return mapFieldChangesToRequestPayload(fieldName, newValue, fieldInfo);
      },
    );
  } else if (
    requestForm.flair__Request_Type__c === RequestFormType.ObjectCreate
  ) {
    return (
      formData.extraFields?.map((extraField) => ({
        fieldName: extraField.fieldApiName,
        fieldType: extraField.type as FieldType,
        newValue: mapFromExtraField(extraField),
      })) ?? []
    );
  }
  return [];
};

const mapToSalaryChangeInput = (
  salaryChange: SalaryChangeData,
): ObjectFieldChangesInput['salaryChange'] | null => {
  if (
    salaryChange.newValue > 0 &&
    !salaryChange.hasPermissionIssue &&
    !salaryChange.hasNoSalary
  ) {
    return {
      currentSalaryId: salaryChange.currentSalaryId ?? null,
      effectiveDate: salaryChange.effectiveDate,
      endDate: salaryChange.endDate ?? null,
      isQuickUpdate: salaryChange.isQuickUpdate,
      newValue: salaryChange.newValue,
      newFrequency: salaryChange.newFrequency ?? null,
      notes: salaryChange.notes ?? null,
    };
  }
  return null;
};

const mapToPositionChangeInput = (
  positionChange: PositionChangeData,
): ObjectFieldChangesInput['positionChange'] | null => {
  if (
    positionChange.newPositionId &&
    positionChange.currentPositionId &&
    positionChange.effectiveDate
  ) {
    return {
      newPositionId: positionChange.newPositionId,
      currentPositionId: positionChange.currentPositionId,
      effectiveDate: positionChange.effectiveDate,
      endDate: positionChange.endDate || null,
    };
  }
  return null;
};

const mapFieldChangesToRequestPayload = (
  fieldName: string,
  newValue: any,
  fieldInfo: ObjectFieldValue | undefined,
) => {
  let newFieldValue = newValue;

  if (fieldInfo?.fieldInfo.type === FieldType.Boolean) {
    newFieldValue = newValue.toString();
  }
  if (fieldInfo?.fieldInfo.type === FieldType.Date) {
    const date = new Date(newValue);
    newFieldValue = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    )
      .toISOString()
      .split('T')[0];
  }

  return {
    fieldName,
    fieldType: fieldInfo?.fieldInfo.type || 'String',
    newValue: newFieldValue,
  };
};

const mapFromExtraField = ({
  internalValue,
  type,
}: ExtraFieldInternalValue): string => {
  return internalValue.value ?? '';
};
